/**
 * Дані й чиста логіка панелі «Сьогодні» (tasks-today.md, розд. 3 і 6) — без React і мережі:
 * - форма відповіді `GET /api/v1/activities/today` (`TodayPage` з `@clientline/contracts`);
 * - перехід рядка в «Прострочено» о `overdueAt` без запиту (TSK-17) і місце лінії «зараз»;
 * - момент наступної зміни вигляду (`overdueAt`, лінія «зараз», північ — `dayEnd`);
 * - лічильник «Задачі N» = `overdue.count + today.count`, межа «99+»;
 * - оптимістичні зміни кешу: «Виконати», «Перенести» і їх «Скасувати»;
 * - варіанти «Перенести» з `dueOptions` сервера: час доби задачі лишається (tasks.md, розд. 7).
 */
import type {
  ActivityCategory,
  Count,
  DueOptions,
  RecordRef,
  TodayItem,
  TodayPage,
} from '@clientline/contracts';
import { fromZonedInput, toZonedInput } from '../../i18n/format.ts';

/** Стан таймера першої реакції в секції `waiting` (tasks-sla.md, розд. 2). */
export type FirstReactionState = 'running' | 'warning' | 'breached';

/**
 * Рядок «Чекають першої реакції» (tasks-sla.md, розд. 4, `WaitingItem`). У контракті відповіді
 * поки `waiting: null`: схему дає E8a-6b разом з таймерами. Форма тут — з документа.
 */
export interface WaitingItem {
  record: RecordRef;
  root: RecordRef | null;
  phone: string | null;
  policy: string;
  state: FirstReactionState;
  startedAt: string;
  warnAt: string | null;
  dueAt: string | null;
  /** До `dueAt` у робочих хвилинах; від'ємне — порушено на стільки; `null` — порогів немає. */
  remainingWorkingMinutes: number | null;
}

export type TodayData = Omit<TodayPage, 'waiting'> & {
  waiting: { items: WaitingItem[]; count: Count } | null;
};

/** Секції панелі, у яких стоїть задача (а не таймер). */
export type TaskSection = 'overdue' | 'today' | 'done';

/** Новий строк: момент або «увесь день» (рівно одне, як у `reschedule`). */
export type TodayDue = { dueAt: string } | { dueDate: string };

const at = (iso: string | null) => (iso ? Date.parse(iso) : Number.POSITIVE_INFINITY);
const byDue = (a: TodayItem, b: TodayItem) => at(a.dueAt) - at(b.dueAt);
const isLate = (item: TodayItem, now: number) =>
  item.overdueAt !== null && Date.parse(item.overdueAt) <= now;

export interface Arranged {
  overdue: TodayItem[];
  today: TodayItem[];
  done: TodayItem[];
  overdueCount: Count;
  todayCount: Count;
  doneCount: Count;
  /** Перед яким рядком `today` лінія «зараз»; `-1` — лінії немає. */
  nowIndex: number;
}

/**
 * Секції на момент `now`: сьогоднішня задача, чий `overdueAt` настав, переходить у «Прострочено»
 * сама, без запиту (TSK-17, о 14:00). Лінія «зараз» — над першою задачею з часом у майбутньому,
 * якщо перед нею є хоч одна сьогоднішня (у прострочених лінія не потрібна: вони всі в минулому).
 */
export function arrange(data: TodayData, now: number): Arranged {
  const late = data.today.items.filter((x) => isLate(x, now));
  const today =
    late.length > 0 ? data.today.items.filter((x) => !isLate(x, now)) : data.today.items;
  const overdue =
    late.length > 0 ? [...data.overdue.items, ...late].sort(byDue) : data.overdue.items;
  const first = today.findIndex((x) => !x.allDay && x.dueAt !== null && Date.parse(x.dueAt) > now);
  return {
    overdue,
    today,
    done: data.done.items,
    overdueCount: shiftCount(data.overdue.count, late.length),
    todayCount: shiftCount(data.today.count, -late.length),
    doneCount: data.done.count,
    nowIndex: first > 0 ? first : -1,
  };
}

/** Лічильник зі зсувом: межа «99+» лишається межею, нижче нуля не буває. */
export function shiftCount(count: Count, by: number): Count {
  if (count.capped || by === 0) return count;
  return { count: Math.max(0, count.count + by), capped: false };
}

/**
 * Найближчий момент після `now`, коли вигляд панелі зміниться сам: `overdueAt` сьогоднішньої
 * задачі, строк задачі (лінія «зараз» зсувається) або північ (`dayEnd`, панель перечитується).
 */
export function nextBoundary(data: TodayData, now: number): number {
  let next = Date.parse(data.dayEnd);
  for (const item of data.today.items) {
    for (const iso of [item.overdueAt, item.allDay ? null : item.dueAt]) {
      const ms = iso ? Date.parse(iso) : Number.NaN;
      if (ms > now && ms < next) next = ms;
    }
  }
  return next;
}

/** Межа лічильника в навігації (tasks-today.md, розд. 3): «99+». */
export const NAV_COUNT_MAX = 99;

export interface TaskCount {
  /** Текст лічильника: число або «99+». */
  text: string;
  /** Є прострочені — лічильник червоний (і словом у доступній назві). */
  overdue: number;
  total: number;
}

/** «Задачі N»: прострочені й сьогоднішні з тієї самої відповіді. `null` — показувати нічого. */
export function taskCount(data: TodayData | undefined, now: number): TaskCount | null {
  if (!data) return null;
  const a = arrange(data, now);
  const total = a.overdueCount.count + a.todayCount.count;
  const capped = a.overdueCount.capped || a.todayCount.capped || total > NAV_COUNT_MAX;
  return {
    text: capped ? `${NAV_COUNT_MAX}+` : String(total),
    overdue: a.overdueCount.count,
    total,
  };
}

/** Де стоїть задача у відповіді. */
export function sectionOf(data: TodayData, id: string): TaskSection | null {
  for (const key of ['overdue', 'today', 'done'] as const) {
    if (data[key].items.some((x) => x.id === id)) return key;
  }
  return null;
}

function without(data: TodayData, key: TaskSection, id: string): TodayData {
  const items = data[key].items.filter((x) => x.id !== id);
  if (items.length === data[key].items.length) return data;
  return { ...data, [key]: { items, count: shiftCount(data[key].count, -1) } };
}

function withItem(data: TodayData, key: TaskSection, item: TodayItem): TodayData {
  const items = [...data[key].items, item];
  if (key === 'done') items.unshift(items.pop() as TodayItem);
  else items.sort(byDue);
  return { ...data, [key]: { items, count: shiftCount(data[key].count, 1) } };
}

/** «Виконати»: задача переходить у «Виконано» (новіша — зверху). */
export function completeIn(data: TodayData, id: string): TodayData {
  const from = sectionOf(data, id);
  if (from === null || from === 'done') return data;
  const item = data[from].items.find((x) => x.id === id) as TodayItem;
  return withItem(without(data, from, id), 'done', { ...item, state: 'done' });
}

/** «Скасувати»: задача повертається туди, де була, з тими самими полями. */
export function restoreIn(data: TodayData, item: TodayItem, to: TaskSection): TodayData {
  const from = sectionOf(data, item.id);
  const base = from === null ? data : without(data, from, item.id);
  return withItem(base, to, item);
}

/** Початок дня `date` (YYYY-MM-DD) у поясі. */
const dayStartIn = (date: string, zone: string) => fromZonedInput(`${date}T00:00`, zone);

/**
 * «Перенести»: новий строк у рядку. Строк після кінця дня — задача зникає з панелі; у межах
 * дня — стоїть за новим часом у «На сьогодні». `overdueAt` рахує сервер (пояс, «увесь день»),
 * тож до відповіді його немає, і рядок сам у «Прострочено» не переходить.
 */
export function rescheduleIn(data: TodayData, id: string, due: TodayDue): TodayData {
  const from = sectionOf(data, id);
  if (from === null || from === 'done') return data;
  const item = data[from].items.find((x) => x.id === id) as TodayItem;
  const next: TodayItem =
    'dueAt' in due
      ? { ...item, dueAt: due.dueAt, allDay: false, dueDate: null, overdueAt: null }
      : {
          ...item,
          dueAt: new Date(dayStartIn(due.dueDate, data.timeZone)).toISOString(),
          allDay: true,
          dueDate: due.dueDate,
          overdueAt: null,
        };
  const rest = without(data, from, id);
  return at(next.dueAt) >= Date.parse(data.dayEnd) ? rest : withItem(rest, 'today', next);
}

/** Версія задачі після відповіді сервера: наступна дія йде з новою передумовою. */
export function withVersion(data: TodayData, id: string, version: number): TodayData {
  const key = sectionOf(data, id);
  if (key === null) return data;
  return {
    ...data,
    [key]: {
      ...data[key],
      items: data[key].items.map((x) => (x.id === id ? { ...x, version } : x)),
    },
  };
}

export interface DueChoice {
  /** Цифра-клавіша меню (`1`…`3`). */
  key: '1' | '2' | '3';
  kind: 'inHour' | 'tomorrow' | 'nextMonday';
  due: TodayDue;
}

/**
 * Варіанти «Перенести» з `dueOptions` (рахує сервер за робочим часом, браузер `worktime` не
 * тягне): «Через годину» — момент; «Завтра» і «Наступного понеділка» — дата, час доби задачі
 * лишається. Задача «на весь день» чи без часу лишається «на весь день».
 */
export function dueChoices(
  item: Pick<TodayItem, 'allDay' | 'dueAt'>,
  options: DueOptions,
  zone: string,
): DueChoice[] {
  const time = item.allDay || !item.dueAt ? null : toZonedInput(item.dueAt, zone).slice(11, 16);
  const on = (date: string): TodayDue =>
    time
      ? { dueAt: new Date(fromZonedInput(`${date}T${time}`, zone)).toISOString() }
      : { dueDate: date };
  return [
    { key: '1', kind: 'inHour', due: { dueAt: options.inHour } },
    { key: '2', kind: 'tomorrow', due: on(options.tomorrow) },
    { key: '3', kind: 'nextMonday', due: on(options.nextMonday) },
  ];
}

/** Іконка рядка за категорією типу (типи й іконки компанії — у формі задачі, E8a-W2). */
export const CATEGORY_ICON = {
  task: 'task',
  call: 'phone',
  meeting: 'calendar',
  email: 'mail',
} as const satisfies Record<ActivityCategory, string>;
